import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Building2,
  ArrowLeft,
  Globe,
  User,
  Check,
  Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SettingsPage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language?.startsWith("es") ? "es" : "en";
  const [selectedLang, setSelectedLang] = useState(currentLang);
  const [saving, setSaving] = useState(false);

  const backPath = user?.user_type === "business" ? "/business" : user?.user_type === "admin" ? "/admin" : "/customer";

  const handleSave = async () => {
    setSaving(true);
    try {
      i18n.changeLanguage(selectedLang);
      localStorage.setItem("i18nextLng", selectedLang);
      await axios.put(`${API}/auth/language`, { language: selectedLang });
      toast.success(selectedLang === "es" ? "Idioma actualizado" : "Language updated");
    } catch {
      toast.error(selectedLang === "es" ? "Error al guardar" : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(backPath)} data-testid="settings-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t("nav.dashboard")}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#0B1F3B] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <span className="font-bold text-[#0B1F3B]">{t("common.appName")}</span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: "Manrope" }} data-testid="settings-heading">
            {t("common.settings")}
          </h1>
          <p className="text-slate-500 mt-1">{user?.email}</p>
        </div>

        {/* Account Info */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#0B1F3B]/10 flex items-center justify-center">
              <User className="w-5 h-5 text-[#0B1F3B]" />
            </div>
            <div>
              <p className="font-semibold text-[#0B1F3B]">{user?.name}</p>
              <p className="text-sm text-slate-500">{user?.user_type === "business" ? t("auth.contractor") : user?.user_type === "admin" ? "Admin" : t("auth.homeowner")}</p>
            </div>
          </div>
        </div>

        {/* Language Preference */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="language-settings">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="font-bold text-[#0B1F3B]">{t("lang.switchLanguage")}</h2>
              <p className="text-sm text-slate-500">
                {currentLang === "es" ? "Selecciona tu idioma preferido" : "Select your preferred language"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => setSelectedLang("en")}
              className={`relative flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                selectedLang === "en"
                  ? "border-[#0B1F3B] bg-[#0B1F3B]/5"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="lang-option-en"
            >
              <span className="text-2xl">🇺🇸</span>
              <div className="text-left">
                <p className="font-semibold text-[#0B1F3B]">English</p>
                <p className="text-xs text-slate-500">United States</p>
              </div>
              {selectedLang === "en" && (
                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#0B1F3B] flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>

            <button
              onClick={() => setSelectedLang("es")}
              className={`relative flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                selectedLang === "es"
                  ? "border-[#0B1F3B] bg-[#0B1F3B]/5"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="lang-option-es"
            >
              <span className="text-2xl">🇲🇽</span>
              <div className="text-left">
                <p className="font-semibold text-[#0B1F3B]">Español</p>
                <p className="text-xs text-slate-500">Estados Unidos</p>
              </div>
              {selectedLang === "es" && (
                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#0B1F3B] flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving || selectedLang === currentLang}
            className="w-full h-12 bg-[#0B1F3B] hover:bg-[#162A4C] text-white font-bold"
            data-testid="save-language-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {selectedLang === "es" ? "Guardando..." : "Saving..."}
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                {t("common.save")}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
